/**
 * GroupChatInput — 群聊输入框组件（仿微信 PC 端图文混排）
 *
 * 核心设计：
 *   - 使用 contentEditable div 实现真正的文本+图片内联混排
 *   - 图片以 inline-block 缩略图形式嵌入在文本之中
 *   - 用户可在图片前后自由输入文字，支持多图连续插入
 *   - DOM 作为内容源，发送时解析 DOM 子节点构建 segments
 *
 * 布局：
 *   ┌──────────────────────────────┐
 *   │ 😊 📎                         │ ← 工具栏
 *   ├──────────────────────────────┤
 *   │ 文字 [图1] 文字 [图2] 文字     │ ← 图文混排区域(contentEditable)
 *   │                              │
 *   ├─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ┤
 *   │                       [发送]  │
 *   └──────────────────────────────┘
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { uploadImage } from '@/service/chat';
import type { ContentSegment } from '@/pages/Demo/GroupChat/types';
import './index.scss';

// ==================== 类型定义 ====================

export interface GroupChatInputMessage {
  segments: ContentSegment[];
}

export interface GroupChatInputProps {
  onSend: (message: GroupChatInputMessage) => void;
  sending?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

interface ImageMeta {
  id: string;
  previewUrl: string;
  url: string | null;
  uploading: boolean;
}

// ==================== 常量 ====================

const EMOJIS: string[] = [
  '\u{1F600}','\u{1F601}','\u{1F602}','\u{1F923}','\u{1F603}',
  '\u{1F604}','\u{1F605}','\u{1F606}','\u{1F609}','\u{1F60A}',
  '\u{1F60B}','\u{1F60E}','\u{1F60D}','\u{1F929}','\u{1F618}',
  '\u{1F61C}','\u{1F92A}','\u{1F61D}','\u{1F911}','\u{1F917}',
  '\u{1F914}','\u{1F610}','\u{1F611}','\u{1F636}','\u{1F60F}',
  '\u{1F612}','\u{1F644}','\u{1F62C}','\u{1F62E}','\u{1F62F}',
  '\u{1F632}','\u{1F633}','\u{1F97A}','\u{1F622}','\u{1F62D}',
  '\u{1F624}','\u{1F621}','\u{1F92C}','\u{1F608}','\u{1F607}',
  '\u{1F920}','\u{1F921}','\u{1F47B}','\u{1F480}','\u{1F47D}',
  '\u{1F916}','\u{1F383}','\u{1F63A}','\u{1F44D}','\u{1F44E}',
  '\u{1F44A}','\u{270A}','\u{1F91B}','\u{1F91C}','\u{1F44F}',
  '\u{1F64C}','\u{1F91D}','\u{1F64F}','\u{270C}','\u{1F91E}',
  '\u{1F44C}','\u{270B}','\u{1F4AA}','\u{2764}','\u{1F9E1}',
  '\u{1F49B}','\u{1F49A}','\u{1F499}','\u{1F49C}','\u{1F5A4}',
  '\u{1F494}','\u{1F495}','\u{1F49E}','\u{1F497}','\u{1F498}',
  '\u{1F49D}','\u{1F389}','\u{1F38A}','\u{1F388}','\u{1F381}',
  '\u{2728}','\u{2B50}','\u{1F31F}','\u{1F525}','\u{1F4AF}',
  '\u{2705}','\u{1F3AF}','\u{1F308}','\u{1F4B0}','\u{1F4BB}',
  '\u{1F4F1}','\u{1F4F7}','\u{1F514}','\u{1F512}','\u{1F511}',
  '\u{1F4CE}','\u{2709}','\u{1F4E9}','\u{1F4C5}','\u23F0',
];

// ==================== 辅助函数 ====================

let _imgSeq = 0;
const genImageId = () => `edimg-${Date.now()}-${++_imgSeq}`;

/**
 * 解析 contentEditable DOM，提取有序片段
 * 遍历 #text 节点和 .gci-img-wrap 元素
 */
const parseSegments = (root: HTMLElement, imageMap: Map<string, ImageMeta>): ContentSegment[] => {
  const result: ContentSegment[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      // 合并相邻连续空格，但保留有意义的内容
      if (text.length > 0) {
        result.push({ type: 'text', content: text });
      }
    } else if (node instanceof HTMLElement && node.classList.contains('gci-img-wrap')) {
      const imgId = node.getAttribute('data-img-id');
      const meta = imgId ? imageMap.get(imgId) : undefined;
      if (meta?.url) {
        result.push({ type: 'image', imageUrl: meta.url });
      }
    } else if (node.hasChildNodes()) {
      node.childNodes.forEach(walk);
    }
  };
  walk(root);
  return result;
};

/**
 * 检查 contentEditable 是否有实际内容
 */
const hasContent = (root: HTMLElement): boolean => {
  const text = root.textContent?.trim() || '';
  if (text.length > 0) return true;
  // 检查是否有图片
  return root.querySelector('.gci-img-wrap') !== null;
};

// ==================== 组件 ====================

const GroupChatInput: React.FC<GroupChatInputProps> = ({
  onSend,
  sending = false,
  placeholder = '输入消息...',
  disabled = false,
}) => {
  const [showEmoji, setShowEmoji] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPanelRef = useRef<HTMLDivElement>(null);
  const imageMapRef = useRef<Map<string, ImageMeta>>(new Map());
  const uploadingRef = useRef(false);

  const isDisabled = disabled || sending;

  // ==================== 内容空状态检测 ====================

  const checkEmpty = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    setIsEmpty(!hasContent(el));
  }, []);

  useEffect(() => {
    checkEmpty();
  }, [checkEmpty]);

  // 表情面板外部点击关闭
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (emojiPanelRef.current && !emojiPanelRef.current.contains(e.target as Node)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // ==================== 光标与选区工具 ====================

  /** 在光标处插入节点 */
  const insertNodeAtCursor = useCallback((node: Node) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    // 确保 range 在 editor 内
    const editor = editorRef.current;
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      // 焦点不在 editor 内，补到末尾
      editor?.focus();
      const lastChild = editor?.lastChild;
      if (lastChild) {
        const r = document.createRange();
        r.setStartAfter(lastChild);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        r.insertNode(node);
        r.setStartAfter(node);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      } else {
        editor?.appendChild(node);
      }
      return;
    }

    range.deleteContents();
    range.insertNode(node);

    // 将光标移到插入节点之后
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  /** 在光标处插入文本 */
  const insertTextAtCursor = useCallback((text: string) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  // ==================== 发送 ====================

  const handleSend = useCallback(() => {
    if (isDisabled) return;

    const editor = editorRef.current;
    if (!editor) return;

    const segments = parseSegments(editor, imageMapRef.current);

    // 过滤掉空文本片段，合并相邻文本段（parseSegments 中每段文本是分离的）
    const merged: ContentSegment[] = [];
    for (const seg of segments) {
      if (seg.type === 'text') {
        const trimmed = seg.content?.trim() || '';
        if (trimmed.length === 0) continue;
        const last = merged[merged.length - 1];
        if (last && last.type === 'text') {
          last.content = (last.content || '') + ' ' + trimmed;
        } else {
          merged.push({ type: 'text', content: trimmed });
        }
      } else {
        merged.push(seg);
      }
    }

    if (merged.length === 0) return;

    onSend({ segments: merged });

    // 清空
    editor.innerHTML = '';
    setIsEmpty(true);

    // 清理图片预览 URL
    imageMapRef.current.forEach((meta) => {
      URL.revokeObjectURL(meta.previewUrl);
    });
    imageMapRef.current.clear();
    uploadingRef.current = false;
  }, [isDisabled, onSend]);

  // ==================== 图片处理 ====================

  /** 创建图片 DOM 节点 */
  const createImageNode = useCallback((meta: ImageMeta): HTMLSpanElement => {
    const wrap = document.createElement('span');
    wrap.className = `gci-img-wrap${meta.uploading ? ' gci-img-wrap-uploading' : ''}`;
    wrap.setAttribute('data-img-id', meta.id);
    wrap.contentEditable = 'false';
    wrap.setAttribute('draggable', 'false');

    const img = document.createElement('img');
    img.className = 'gci-img-thumb';
    img.src = meta.previewUrl;
    img.alt = '图片';
    img.draggable = false;
    wrap.appendChild(img);

    if (meta.uploading) {
      const loading = document.createElement('span');
      loading.className = 'gci-img-loading';
      loading.innerHTML = '<span></span>';
      wrap.appendChild(loading);
    }

    const btn = document.createElement('button');
    btn.className = 'gci-img-remove';
    btn.textContent = '\u2715';
    btn.setAttribute('type', 'button');
    btn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = wrap.getAttribute('data-img-id');
      if (id) {
        const metaDel = imageMapRef.current.get(id);
        if (metaDel) {
          URL.revokeObjectURL(metaDel.previewUrl);
          imageMapRef.current.delete(id);
        }
        wrap.remove();
        checkEmpty();
        // 检测是否全部上传完毕
        uploadingRef.current = Array.from(imageMapRef.current.values()).some((m) => m.uploading);
        editorRef.current?.focus();
      }
    });
    wrap.appendChild(btn);

    return wrap;
  }, [checkEmpty]);

  /** 更新图片状态（上传完成后替换 loading） */
  const updateImageNode = useCallback((id: string, url: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const wrap = editor.querySelector(`.gci-img-wrap[data-img-id="${id}"]`) as HTMLElement | null;
    if (!wrap) return;

    wrap.classList.remove('gci-img-wrap-uploading');
    const loadingEl = wrap.querySelector('.gci-img-loading');
    if (loadingEl) loadingEl.remove();

    // 更新 map
    const meta = imageMapRef.current.get(id);
    if (meta) {
      meta.url = url;
      meta.uploading = false;
    }

    uploadingRef.current = Array.from(imageMapRef.current.values()).some((m) => m.uploading);
  }, []);

  /** 图片上传流程 */
  const uploadAndInsert = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) { alert('请选择图片文件'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('图片大小不能超过 10MB'); return; }

    const id = genImageId();
    const previewUrl = URL.createObjectURL(file);
    const meta: ImageMeta = { id, previewUrl, url: null, uploading: true };
    imageMapRef.current.set(id, meta);
    uploadingRef.current = true;

    // 在光标处插入预览节点
    const node = createImageNode(meta);
    insertNodeAtCursor(node);
    checkEmpty();

    // 确保 editor 聚焦
    editorRef.current?.focus();

    try {
      const { url } = await uploadImage(file);
      updateImageNode(id, url);
    } catch (err: any) {
      alert(err.message || '图片上传失败');
      // 移除
      const wrap = editorRef.current?.querySelector(`.gci-img-wrap[data-img-id="${id}"]`);
      if (wrap) wrap.remove();
      URL.revokeObjectURL(previewUrl);
      imageMapRef.current.delete(id);
      checkEmpty();
      uploadingRef.current = Array.from(imageMapRef.current.values()).some((m) => m.uploading);
    }
  }, [createImageNode, insertNodeAtCursor, updateImageNode, checkEmpty]);

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    uploadAndInsert(file);
  }, [uploadAndInsert]);

  // ==================== 表情 ====================

  const handleEmojiSelect = useCallback((emoji: string) => {
    insertTextAtCursor(emoji);
    checkEmpty();
    editorRef.current?.focus();
  }, [insertTextAtCursor, checkEmpty]);

  // ==================== 键盘处理 ====================

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Enter 发送（Shift+Enter 换行）
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
        return;
      }

      // Backspace / Delete：如果光标紧邻图片，删除图片
      if (e.key === 'Backspace' || e.key === 'Delete') {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);

        if (e.key === 'Backspace') {
          // 检查光标前方是否紧邻图片
          if (range.collapsed) {
            const node = range.startContainer;
            const prevSibling = node.previousSibling;
            if (prevSibling instanceof HTMLElement && prevSibling.classList.contains('gci-img-wrap')) {
              e.preventDefault();
              const id = prevSibling.getAttribute('data-img-id');
              if (id) {
                const meta = imageMapRef.current.get(id);
                if (meta) { URL.revokeObjectURL(meta.previewUrl); imageMapRef.current.delete(id); }
              }
              prevSibling.remove();
              checkEmpty();
              uploadingRef.current = Array.from(imageMapRef.current.values()).some((m) => m.uploading);
              return;
            }
            // 如果是位于图片之后且光标在文本节点开头
            if (node.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
              const prevEl = (node as Element).previousElementSibling;
              if (prevEl instanceof HTMLElement && prevEl.classList.contains('gci-img-wrap')) {
                e.preventDefault();
                const id = prevEl.getAttribute('data-img-id');
                if (id) {
                  const meta = imageMapRef.current.get(id);
                  if (meta) { URL.revokeObjectURL(meta.previewUrl); imageMapRef.current.delete(id); }
                }
                prevEl.remove();
                checkEmpty();
                uploadingRef.current = Array.from(imageMapRef.current.values()).some((m) => m.uploading);
                return;
              }
            }
          }
        }
      }
    },
    [handleSend, checkEmpty]
  );

  // 监听输入变化以更新空状态（debounce by rAF）
  const handleInput = useCallback(() => {
    requestAnimationFrame(checkEmpty);
  }, [checkEmpty]);

  // ==================== 粘贴处理 ====================

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          uploadAndInsert(file);
        }
        return;
      }
    }
    // 非图片粘贴走默认行为（粘贴纯文本）
  }, [uploadAndInsert]);

  // ==================== 拖拽图片到输入框 ====================

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        uploadAndInsert(file);
      }
    }
  }, [uploadAndInsert]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  // ==================== 渲染 ====================

  return (
    <div
      className={`group-chat-input${isDisabled ? ' group-chat-input-disabled' : ''}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* 工具栏 */}
      <div className="gci-toolbar">
        <button
          className={`gci-tool-btn${showEmoji ? ' active' : ''}`}
          onClick={() => setShowEmoji((v) => !v)}
          disabled={isDisabled}
          title="表情"
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
        </button>
        <button
          className="gci-tool-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={isDisabled}
          title="插入图片"
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* 表情面板 */}
      {showEmoji && (
        <div className="gci-emoji-panel" ref={emojiPanelRef}>
          <div className="gci-emoji-grid">
            {EMOJIS.map((emoji, i) => (
              <button key={i} className="gci-emoji-item" onClick={() => handleEmojiSelect(emoji)} type="button">
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 图文混排区域 — contentEditable */}
      <div
        ref={editorRef}
        className={`gci-editor${isEmpty ? ' gci-editor-empty' : ''}`}
        contentEditable={!isDisabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => {
          // 聚焦时进入编辑状态
        }}
      />

      {/* 底部栏 */}
      <div className="gci-footer">
        <span className="gci-hint">Enter 发送 · Shift+Enter 换行 · 支持粘贴/拖拽图片</span>
        <button
          className="gci-send"
          onClick={handleSend}
          disabled={isDisabled || isEmpty}
          type="button"
        >
          发送
        </button>
      </div>
    </div>
  );
};

export default GroupChatInput;